// Kiosk settings — change weighbridge, view simulator state, set up a PIN,
// manually refresh cached data, and log out. Reached via the gear icon in
// the top bar.

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../../../api/queryKeys';
import { KioskPinSetup } from './KioskPinSetup';
import styles from './kiosk.module.css';

export interface KioskSettingsProps {
  readonly weighbridgeName: string | null;
  readonly simulator: boolean;
  /** Re-confirm by clearing the lock + signing out, so a new selection
   *  requires re-authentication (guards against accidental switches). */
  readonly onChangeWeighbridge: () => void;
  readonly onLogout: () => void;
  readonly onBack: () => void;
}

export function KioskSettings({
  weighbridgeName,
  simulator,
  onChangeWeighbridge,
  onLogout,
  onBack,
}: KioskSettingsProps) {
  const queryClient = useQueryClient();
  const [justRefreshed, setJustRefreshed] = useState(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  function handleRefreshData(): void {
    // Broad wb.all covers every wb.* query (tickets, products, vat-rates,
    // customer settings, etc.) via TanStack Query's partial-key matching.
    // This intentionally also refetches a background workspace tab's open
    // ticket query — that's the desired "refresh everything" behavior for
    // this button, not a side effect to guard against.
    // The kiosk customer quick-pick cache lives under a different top-level
    // key (['customers', ...], warmed at queryKeys.customers.list({ limit: 100 })
    // in KioskRoot's login prefetch — S63G Task 3) so it needs its own invalidation.
    void queryClient.invalidateQueries({ queryKey: queryKeys.wb.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.customers.list({ limit: 100 }) });

    setJustRefreshed(true);
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => setJustRefreshed(false), 2000);
  }

  return (
    <div className={styles.stepBody}>
      <div className={styles.listHeader}>
        <h2>Settings</h2>
        <button type="button" className={styles.secondaryBtn} onClick={onBack}>
          ← Back
        </button>
      </div>

      <div className={styles.reviewRow}>
        <span>Current weighbridge</span>
        <strong>{weighbridgeName ?? '—'}</strong>
      </div>
      <button
        type="button"
        className={styles.secondaryBtn}
        onClick={() => {
          // eslint-disable-next-line no-alert
          if (window.confirm('Change weighbridge? You will need to sign in again.')) {
            onChangeWeighbridge();
          }
        }}
      >
        Change weighbridge
      </button>

      <div className={styles.reviewRow}>
        <span>Simulator mode</span>
        <strong>{simulator ? 'On' : 'Off'}</strong>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button
          type="button"
          className={styles.secondaryBtn}
          data-testid="kiosk-refresh-data"
          onClick={handleRefreshData}
        >
          Refresh data
        </button>
        {justRefreshed ? (
          <span style={{ color: 'var(--kiosk-success)', fontWeight: 600 }} aria-live="polite">
            Refreshed
          </span>
        ) : null}
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem' }}>
        <h3 style={{ marginTop: 0, color: '#0f172a' }}>Kiosk PIN</h3>
        <KioskPinSetup />
      </div>

      <button type="button" className={styles.dangerBtn} onClick={onLogout}>
        Log out
      </button>
    </div>
  );
}
